import { useEffect, useMemo, useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { InfoCard, Progress } from '@backstage/core-components';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DatadogSeries, GraphDef, queryTimeseries, windowedValue } from './api';

const formatValue = (value: number) => {
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(1)}k`;
  if (abs >= 100) return value.toFixed(0);
  if (abs >= 1) return `${Number(value.toFixed(2))}`;
  if (abs === 0) return '0';
  return value.toPrecision(2);
};

const formatTime = (ts: number, rangeSeconds: number) => {
  const date = new Date(ts);
  const time = date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  if (rangeSeconds > 24 * 3600) {
    return `${date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
    })} ${time}`;
  }
  return time;
};

// A custom per-metric value (label or color) from the graph's maps: exact
// metric-name match first, then any key contained in the series
// expression/display name.
const customValue = (s: DatadogSeries, map?: Record<string, string>) => {
  if (!map) {
    return undefined;
  }
  if (s.metric && map[s.metric]) {
    return map[s.metric];
  }
  const haystack = `${s.expression ?? ''} ${s.display_name ?? ''}`;
  const hit = Object.keys(map).find(key => haystack.includes(key));
  return hit ? map[hit] : undefined;
};

// One line per series; a query with `by {...}` gets its group tags as label.
const seriesLabel = (s: DatadogSeries, labels?: Record<string, string>) => {
  const custom = customValue(s, labels);
  const scope = s.scope && s.scope !== '*' ? s.scope : undefined;
  if (custom) {
    return scope ? `${custom} · ${scope}` : custom;
  }
  return scope ?? s.display_name ?? s.metric ?? s.expression ?? 'value';
};

// Line colors tuned for the SAVR palette in both themes.
const lineColors = (dark: boolean) =>
  dark
    ? ['#8F93FF', '#34D399', '#F5A623', '#FF385F', '#5EC8E5', '#B78CFF']
    : ['#4E4CCD', '#059669', '#D97706', '#E11D48', '#0E7490', '#7C3AED'];

/**
 * Present and non-null.
 *
 * Spelled out because the three things this distinguishes are all live here and
 * `!= null` collapsed them: a row's value is `null` where the series has a gap,
 * and `undefined` where the index is off the end of the array. Both mean "no
 * neighbour to draw a line to". A plain `!== null` would call an off-the-end
 * neighbour a VALUE, which inverts the test below at the first and last
 * datapoint of every series - so this exists to make the eqeqeq fix provably
 * equivalent rather than merely plausible.
 *
 * cx/cy are typed `number | undefined`, but recharts is JavaScript and the type
 * does not bind it, so they go through the same check rather than relying on a
 * declaration a third party never made.
 *
 * A type predicate rather than a plain boolean, so the guard still narrows its
 * argument the way the `== null` it replaces did.
 */
const hasValue = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined;

// recharts calls the dot renderer for every datapoint; draw a small circle
// only where a point has no drawable neighbors (an isolated sample in a
// sparse count series - without it the point is invisible, since no line
// segment can reach it).
export const isolatedPointDot =
  (rows: Record<string, number | null>[], dataKey: string, color: string) =>
  (props: { cx?: number; cy?: number; index?: number }) => {
    const { cx, cy, index = 0 } = props;
    const prev = rows[index - 1]?.[dataKey];
    const next = rows[index + 1]?.[dataKey];
    if (!hasValue(cx) || !hasValue(cy) || hasValue(prev) || hasValue(next)) {
      return <g key={`${dataKey}-${index}`} />;
    }
    return (
      <circle
        key={`${dataKey}-${index}`}
        cx={cx}
        cy={cy}
        r={2.5}
        fill={color}
      />
    );
  };

const useStyles = makeStyles(theme => ({
  chartWrapper: {
    height: 220,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: 13,
    wordBreak: 'break-word',
  },
  noData: {
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  centered: {
    height: 220,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0, 2),
  },
  toplist: {
    height: 220,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    paddingRight: theme.spacing(1),
  },
  toplistRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  toplistLabel: {
    fontSize: 12,
    color: theme.palette.text.primary,
    width: '38%',
    flexShrink: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'right',
  },
  toplistTrack: {
    flex: 1,
    height: 14,
    borderRadius: 4,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : 'rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  toplistBar: {
    height: '100%',
    borderRadius: 4,
  },
  toplistValue: {
    fontSize: 12,
    fontWeight: 600,
    width: 80,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
}));

export function GraphCard({
  graph,
  rangeSeconds,
  reloadKey,
  onEdit,
  onDelete,
}: {
  graph: GraphDef;
  rangeSeconds: number;
  reloadKey: number;
  /** Omitted for read-only viewers (external users). */
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const classes = useStyles();
  const theme = useTheme();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [series, setSeries] = useState<DatadogSeries[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    const to = Math.floor(Date.now() / 1000);
    queryTimeseries(
      { discoveryApi, fetchApi },
      graph.query,
      to - rangeSeconds,
      to,
    )
      .then(result => {
        if (!cancelled) {
          setSeries(result);
          setLoading(false);
        }
      })
      .catch(e => {
        if (!cancelled) {
          setError(String(e.message ?? e));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, graph.query, rangeSeconds, reloadKey]);

  // Merge the series into recharts rows keyed by timestamp; series can have
  // different timestamps (different rollup intervals), so gaps stay null.
  const rows = useMemo(() => {
    const byTs = new Map<number, Record<string, number | null>>();
    series.forEach((s, index) => {
      for (const [ts, value] of s.pointlist ?? []) {
        const row = byTs.get(ts) ?? { ts };
        row[`s${index}`] = value;
        byTs.set(ts, row);
      }
    });
    return [...byTs.values()].sort((a, b) => (a.ts ?? 0) - (b.ts ?? 0));
  }, [series]);

  const dark = theme.palette.type === 'dark';
  const colors = lineColors(dark);
  const unit = series[0]?.unit?.[0]?.short_name;

  // Pie / top list need one value per series: the graph's aggregation
  // applied over the whole range (falls back to sum for sum: queries -
  // "clicks per subject" style counters - and avg otherwise).
  const totals = useMemo(() => {
    const aggregation =
      graph.aggregation ?? (graph.query.startsWith('sum:') ? 'sum' : 'avg');
    return series
      .map(s => ({
        label: seriesLabel(s, graph.labels),
        value: windowedValue(
          (s.pointlist ?? [])
            .map(([, v]) => v)
            .filter((v): v is number => typeof v === 'number'),
          aggregation,
        ),
      }))
      .filter(
        (t): t is { label: string; value: number } => t.value !== undefined,
      )
      .sort((a, b) => b.value - a.value);
  }, [series, graph.labels, graph.aggregation, graph.query]);

  let content;
  if (loading) {
    content = (
      <div className={classes.centered}>
        <Progress style={{ width: '60%' }} />
      </div>
    );
  } else if (error) {
    content = (
      <div className={classes.centered}>
        <Typography className={classes.errorText}>{error}</Typography>
      </div>
    );
  } else if (rows.length === 0) {
    content = (
      <div className={classes.centered}>
        <Typography className={classes.noData}>
          No datapoints for this query in the selected time range.
        </Typography>
      </div>
    );
  } else if (graph.chartType === 'toplist') {
    const max = totals[0]?.value || 1;
    content = (
      <div className={classes.toplist}>
        {totals.slice(0, 12).map((total, index) => (
          <div key={total.label} className={classes.toplistRow}>
            <span className={classes.toplistLabel} title={total.label}>
              {total.label}
            </span>
            <div className={classes.toplistTrack}>
              <div
                className={classes.toplistBar}
                style={{
                  width: `${Math.max((total.value / max) * 100, 1.5)}%`,
                  backgroundColor: colors[index % colors.length],
                }}
              />
            </div>
            <span className={classes.toplistValue}>
              {formatValue(total.value)}
              {unit ? ` ${unit}` : ''}
            </span>
          </div>
        ))}
      </div>
    );
  } else if (graph.chartType === 'pie') {
    // Top slices only; everything else lumps into "Other" so the pie stays
    // readable on high-cardinality group-bys.
    const top = totals.slice(0, 7);
    const rest = totals.slice(7).reduce((sum, t) => sum + t.value, 0);
    const slices = rest > 0 ? [...top, { label: 'Other', value: rest }] : top;
    content = (
      <div className={classes.chartWrapper}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              innerRadius="52%"
              outerRadius="82%"
              paddingAngle={2}
              stroke="none"
              isAnimationActive={false}
            >
              {slices.map((slice, index) => (
                <Cell
                  key={slice.label}
                  fill={colors[index % colors.length]}
                  opacity={slice.label === 'Other' ? 0.45 : 1}
                />
              ))}
            </Pie>
            <ChartTooltip
              formatter={value =>
                typeof value === 'number'
                  ? `${formatValue(value)}${unit ? ` ${unit}` : ''}`
                  : '-'
              }
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
                fontSize: 12,
                color: theme.palette.text.primary,
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: 11 }}
              formatter={(value: string) => (
                <span style={{ color: theme.palette.text.secondary }}>
                  {value}
                </span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  } else {
    content = (
      <div className={classes.chartWrapper}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={rows}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          >
            <CartesianGrid stroke={theme.palette.divider} vertical={false} />
            <XAxis
              dataKey="ts"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={ts => formatTime(ts, rangeSeconds)}
              tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
              stroke={theme.palette.divider}
              tickLine={false}
              label={
                graph.xLabel
                  ? {
                      value: graph.xLabel,
                      position: 'insideBottom',
                      offset: -4,
                      fill: theme.palette.text.secondary,
                      fontSize: 11,
                    }
                  : undefined
              }
            />
            <YAxis
              width={graph.yLabel ? 64 : 48}
              tickFormatter={formatValue}
              tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
              stroke={theme.palette.divider}
              tickLine={false}
              axisLine={false}
              label={
                graph.yLabel
                  ? {
                      value: graph.yLabel,
                      angle: -90,
                      position: 'insideLeft',
                      fill: theme.palette.text.secondary,
                      fontSize: 11,
                    }
                  : undefined
              }
            />
            <ChartTooltip
              labelFormatter={ts => new Date(Number(ts)).toLocaleString()}
              formatter={value =>
                typeof value === 'number'
                  ? `${formatValue(value)}${unit ? ` ${unit}` : ''}`
                  : '-'
              }
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
                fontSize: 12,
                color: theme.palette.text.primary,
              }}
            />
            {series.length > 1 && series.length <= 8 && (
              <Legend
                wrapperStyle={{ fontSize: 11 }}
                formatter={(value: string) => (
                  <span style={{ color: theme.palette.text.secondary }}>
                    {value}
                  </span>
                )}
              />
            )}
            {series.map((s, index) => {
              const color =
                (!graph.groupBy && customValue(s, graph.colors)) ||
                colors[index % colors.length];
              return (
                <Line
                  key={`s${index}`}
                  dataKey={`s${index}`}
                  name={seriesLabel(s, graph.labels)}
                  type="monotone"
                  // Sparse count series have datapoints with null neighbors;
                  // without a dot those points are invisible (no line segment
                  // can be drawn to them).
                  dot={isolatedPointDot(rows, `s${index}`, color)}
                  strokeWidth={2}
                  // Custom per-metric colors don't apply to group-by graphs,
                  // where every line is the same metric.
                  stroke={color}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <InfoCard
      title={graph.title}
      subheader={unit ? `in ${unit}` : undefined}
      action={
        onEdit || onDelete ? (
          <>
            {onEdit && (
              <Tooltip title="Edit graph">
                <IconButton size="small" onClick={onEdit}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Remove graph">
                <IconButton size="small" onClick={onDelete}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        ) : undefined
      }
    >
      {content}
    </InfoCard>
  );
}
